import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Typography,
} from "@mui/material";

import { useAuth } from "../hooks/useAuth";

const PENDING_TOKEN_KEY = "janus_pending_token";
const PENDING_TENANTS_KEY = "janus_pending_tenants";
const REMEMBERED_TENANT_KEY = "janus_remembered_tenant_id";

// Must match STORAGE_KEY in useTenant.tsx — this is the key the TenantProvider
// reads to decide which membership becomes the active workspace after login.
const ACTIVE_TENANT_KEY = "janus_active_tenant_id";

export default function TenantSelect() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [tenants, setTenants] = useState<PendingAuth["tenants"]>([]);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem(PENDING_TOKEN_KEY);
    const raw = sessionStorage.getItem(PENDING_TENANTS_KEY);

    if (!token || !raw) {
      navigate("/", { replace: true });
      return;
    }

    try {
      const parsed = JSON.parse(raw) as PendingAuth["tenants"];
      const rememberedId = localStorage.getItem(REMEMBERED_TENANT_KEY);

      if (rememberedId) {
        const match = parsed.find((t) => String(t.id) === rememberedId);
        if (match) {
          sessionStorage.removeItem(PENDING_TOKEN_KEY);
          sessionStorage.removeItem(PENDING_TENANTS_KEY);
          setPendingToken(token);
          setTenants(parsed);
          selectTenant(token, match.id, true);
          return;
        }
      }

      setPendingToken(token);
      setTenants(parsed);
    } catch {
      navigate("/", { replace: true });
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTenant = async (
    token: string,
    tenantId: number,
    saveRemembered: boolean,
  ) => {
    setSelecting(true);
    setError("");

    try {
      const baseUrl = process.env.REACT_APP_API_URL ?? "";
      const response = await fetch(`${baseUrl}/platform/self/tenant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ tenant_id: tenantId }),
      });

      if (!response.ok) {
        localStorage.removeItem(REMEMBERED_TENANT_KEY);
        const body = await response.json().catch(() => ({}));
        const detail = (body as { detail?: string | { message?: string } })
          .detail;
        const msg = typeof detail === "string" ? detail : detail?.message;
        setError(msg ?? "Failed to select tenant. Please try again.");
        return;
      }

      if (saveRemembered) {
        localStorage.setItem(REMEMBERED_TENANT_KEY, String(tenantId));
      } else {
        localStorage.removeItem(REMEMBERED_TENANT_KEY);
      }

      // Record the chosen tenant under the key TenantProvider reads, so it
      // activates this workspace instead of falling back to the first
      // membership returned by the API.
      localStorage.setItem(ACTIVE_TENANT_KEY, String(tenantId));

      sessionStorage.removeItem(PENDING_TOKEN_KEY);
      sessionStorage.removeItem(PENDING_TENANTS_KEY);

      await refresh();
      navigate("/app");
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setSelecting(false);
    }
  };

  if (!pendingToken) {
    return null;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Typography variant="h5" gutterBottom>
        {t("tenantSelect.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("tenantSelect.description")}
      </Typography>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
        }
        label={t("tenantSelect.rememberSelection")}
        sx={{ mb: 1 }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {tenants.map((tenant) => (
          <Card key={tenant.id} variant="outlined">
            <CardActionArea
              onClick={() =>
                pendingToken && selectTenant(pendingToken, tenant.id, remember)
              }
              disabled={selecting}
            >
              <CardContent>
                <Typography variant="h6">{tenant.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {tenant.slug}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {selecting && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
    </Container>
  );
}
